import { Sparkles, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useUnreadSignals, useMarkSignal } from '../hooks/useApi';
import { groupSignalsByJob } from '../utils/group-signals-by-job';

/**
 * Dashboard "New Matches" (Background Signals Engine Phase 1, regrouped by
 * US-642/US-643).
 *
 * Signals arrive in per-job batches from the signal-detector scheduler, so a
 * flat list reads as runs of identical subtitles — 89 rows that are really a
 * handful of jobs. This renders ONE CARD PER JOB instead, capped at 6, and
 * links onward to pages that already exist. The dashboard is glance-and-jump;
 * triage happens on `/jobs/:id/sourcing`.
 *
 * Fails safe: renders nothing on error or while empty — never blocks the rest
 * of the dashboard. Dismiss stays PER SIGNAL: a bulk dismiss would let one
 * click discard dozens of real matches with no undo.
 */

/** Grouping alone does not bound the page — 89 signals across 40 jobs is 40 cards. */
export const MAX_JOB_CARDS = 6;

export function SignalsBadge() {
  const { data, isError, isLoading } = useUnreadSignals();
  const markSignal = useMarkSignal();

  if (isLoading || isError) return null;
  const signals = data?.signals ?? [];
  if (signals.length === 0) return null;
  // US-645: the endpoint pages now, so signals.length is the PAGE size. The
  // badge must report the server's true total or it silently under-counts
  // once unread exceeds one page. Fall back to the page length only if an
  // older/other response shape omits `total`.
  const totalUnread = data?.total ?? signals.length;

  const groups = groupSignalsByJob(signals);
  const visible = groups.slice(0, MAX_JOB_CARDS);
  const hiddenJobs = groups.length - visible.length;

  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-3.5 w-3.5 text-amber-400" />
        <span className="text-xs font-semibold text-gray-400 tracking-wider uppercase">
          New Matches
        </span>
        <span
          data-testid="new-matches-total"
          className="ml-1 bg-amber-600 text-white text-xs font-semibold px-1.5 py-0.5 rounded-full"
        >
          {totalUnread}
        </span>
      </div>

      <div
        data-testid="signal-job-grid"
        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3"
      >
        {visible.map((group) => {
          const remainder = group.count - group.visibleSignals.length;

          return (
            <div
              key={group.key}
              data-testid="signal-job-card"
              className="bg-gray-800 border border-white/[0.07] rounded-xl px-4 py-3 flex flex-col gap-2 min-w-0"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium text-gray-100 truncate min-w-0">
                  {group.jobTitle}
                </p>
                <span
                  data-testid="signal-job-count"
                  className="flex-shrink-0 bg-amber-600 text-white text-xs font-semibold px-1.5 py-0.5 rounded-full"
                >
                  {group.count}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-400 min-w-0">
                {group.stars !== null && (
                  <span className="flex-shrink-0">{group.stars}★</span>
                )}
                {group.visibleSignals.map((signal) => (
                  <span key={signal.id} className="inline-flex items-center gap-1 min-w-0">
                    <span className="truncate">
                      {signal.candidateName ?? 'Unknown candidate'}
                    </span>
                    <button
                      onClick={() => markSignal.mutate({ id: signal.id, status: 'dismissed' })}
                      className="text-gray-500 hover:text-gray-300 flex-shrink-0"
                      aria-label={`Dismiss ${signal.candidateName ?? 'Unknown candidate'}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                {remainder > 0 && <span className="flex-shrink-0">+{remainder}</span>}
              </div>

              {group.linkTo !== null && (
                <Link
                  to={group.linkTo}
                  className="text-xs text-amber-400 hover:text-amber-300 mt-auto"
                >
                  View all {group.count} →
                </Link>
              )}
            </div>
          );
        })}
      </div>

      {/* This link sits on the page background, not inside a dark card, so it
          needs a light-mode colour of its own — amber-400 on bg-surface-50
          (#fafaf9) computes to 1.60:1, against WCAG AA's 4.5:1 for body text. */}
      {hiddenJobs > 0 && (
        <Link
          to="/jobs"
          className="inline-block mt-3 text-xs text-amber-700 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300"
        >
          +{hiddenJobs} more jobs →
        </Link>
      )}
    </section>
  );
}
